import { useLazyQuery } from '@apollo/client';
import { useEffect, useState, useRef } from 'react';

import { RecommendationType } from '@xing-com/crate-common-graphql-types';
import { useLocation } from '@xing-com/crate-core-router';
import {
  GetSsTakenJobsLoggedOutDocument,
  GetSsAvailableJobsLoggedOutDocument,
} from '@xing-com/crate-dream-machine-graphql';
import { serialize } from '@xing-com/crate-dream-machine-hooks';
import type {
  CardData,
  HookData,
  FatalErrorState,
} from '@xing-com/crate-dream-machine-search-and-shuffle';
import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';

const randomize = <T>(array: T[]): T[] => {
  return array
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
};

export const useSearchAndSwipeData: HookData = (filter) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [interactedCards, setInteractedCards] = useLocalStorage<CardData[]>(
    'dm-interacted-cards-logged-out',
    []
  );
  const [cards, setCards] = useState<CardData[]>([]);
  const [fatalError, setFatalError] = useState<FatalErrorState | undefined>();
  const isFirstData = useRef(true);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const recoType =
    filter === RecommendationType.All ||
    filter === RecommendationType.Available ||
    filter === RecommendationType.Taken
      ? filter
      : RecommendationType.All;

  const [fetchTakenDreamJobs, { data: takenData }] = useLazyQuery(
    GetSsTakenJobsLoggedOutDocument,
    {
      variables: { recoType: RecommendationType.Taken },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    }
  );
  const [fetchAvailableDreamJobs, { data: availableData }] = useLazyQuery(
    GetSsAvailableJobsLoggedOutDocument,
    {
      variables: { recoType: RecommendationType.Available },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    }
  );

  useEffect(() => {
    if (
      recoType === RecommendationType.Taken ||
      recoType === RecommendationType.All
    ) {
      fetchTakenDreamJobs();
    }
    if (
      recoType === RecommendationType.Available ||
      recoType === RecommendationType.All
    ) {
      fetchAvailableDreamJobs();
    }
  }, [fetchTakenDreamJobs, fetchAvailableDreamJobs, recoType]);

  useEffect(() => {
    if (
      takenData?.dreamJobsFetchSearchSwipeRecommendation?.collection?.length ===
        0 &&
      isFirstData.current
    ) {
      setFatalError({
        type: 'fatalErrorState',
        headerKey: 'SEARCH_AND_SHUFFLE_JOB_CARD_ERROR_RECOMMENDATIONS_TITLE',
        descriptionKey:
          'SEARCH_AND_SHUFFLE_JOB_CARD_ERROR_RECOMMENDATIONS_DESCRIPTION',
        mainButton: {
          labelKey: 'SEARCH_AND_SHUFFLE_JOB_CARD_ERROR_RECOMMENDATIONS_BUTTON',
          onClick: () => {
            window.location.assign('/jobseeker-criteria');
          },
        },
        secondaryButton: {
          labelKey:
            'SEARCH_AND_SHUFFLE_JOB_CARD_ERROR_RECOMMENDATIONS_BUTTON_SECONDARY',
          onClick: () => {
            window.location.assign('/profile');
          },
        },
      });
      return;
    }

    let dreamJobsCards;

    // All cards
    if (takenData && availableData && recoType === RecommendationType.All) {
      dreamJobsCards = serialize(
        [
          ...(
            takenData.dreamJobsFetchSearchSwipeRecommendation?.collection || []
          ).filter((item) => item?.__typename === 'TakenJob'),
          ...(
            availableData.dreamJobsFetchSearchSwipeRecommendation?.collection ||
            []
          ).filter((item) => item?.__typename === 'AvailableJob'),
        ],
        ['']
      ).filter(
        (item) =>
          item.company &&
          (item.type === 'AvailableJob' ||
            (item.type === 'TakenJob' && item.similarMembers?.length))
      );

      if (searchParams.get('disable_randomize') !== 'true') {
        dreamJobsCards = randomize(dreamJobsCards);
      }

      // Only taken cards
    } else if (takenData && recoType === RecommendationType.Taken) {
      dreamJobsCards = serialize(
        takenData.dreamJobsFetchSearchSwipeRecommendation?.collection?.filter(
          (item) => item?.__typename === 'TakenJob'
        ),
        ['']
      );

      // Only available cards
    } else if (availableData && recoType === RecommendationType.Available) {
      dreamJobsCards = serialize(
        availableData.dreamJobsFetchSearchSwipeRecommendation?.collection?.filter(
          (item) => item?.__typename === 'AvailableJob'
        ),
        ['']
      );
    }

    if (dreamJobsCards) {
      const cards: CardData[] = dreamJobsCards
        .map((jobCard) => ({
          type: jobCard.type,
          data: { ...jobCard },
        }))
        .filter(
          (card) => !interactedCards.some((c) => c.data.id === card.data.id)
        );

      isFirstData.current = false;
      setCards(cards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takenData, availableData, recoType]);

  return {
    data: cards,
    loading: false,
    error: null,
    fetchMore: () => {
      if (
        recoType === RecommendationType.Taken ||
        recoType === RecommendationType.All
      ) {
        fetchTakenDreamJobs({
          variables: { recoType: RecommendationType.Taken },
        });
      }
      if (
        recoType === RecommendationType.Available ||
        recoType === RecommendationType.All
      ) {
        fetchAvailableDreamJobs({
          variables: { recoType: RecommendationType.Available },
        });
      }
    },
    fatalError,
  };
};
